'use client';

import { ReactNode } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { HtmlParserOptions, HtmlRendererProps } from '@/features/cms/interface';

function isExternalLink(href: string): boolean {
  if (!href || href.startsWith('#') || href.startsWith('/') || href.startsWith('?')) {
    return false;  
  }
  
   if (href.startsWith('//')) {
    return true;  
  }
  
   if (href.startsWith('http://') || href.startsWith('https://')) {
    try {
      const url = new URL(href);
      
      if (typeof window !== 'undefined') {
        return url.origin !== window.location.origin;
      }
       return true;
    } catch {
      return false;  
    }
  }
  
  return false;
}

const defaultClasses: Required<HtmlParserOptions> = {
  h1Classes: 'text-3xl extrabold py-4 text-center uppercase font-title',
  h2Classes: 'text-2xl font-semibold py-4 font-title',
  h3Classes: 'text-xl font-semibold py-3 font-title',
  h4Classes: 'text-xl font-semibold py-3 font-title',
  h5Classes: 'text-xl font-semibold py-3 font-title',
  h6Classes: 'text-xl font-semibold py-3 font-title',
  pClasses: 'mb-4 font-normal text-white',
  ulClasses: 'list-disc pl-6 mb-4 space-y-6 font-text',
  liClasses: 'text-white mb-2',
  aClasses:
    'text-hprimary-500 underline hover:text-hprimary-700 transition-colors mx-1 font-medium font-text',
  strongClasses: 'font-bold font-title my-2',
  imgClasses: 'max-w-full h-auto rounded-lg my-4 mx-auto block',
};

function HtmlRenderer({ htmlString, customClasses = {} }: HtmlRendererProps) {
  if (!htmlString) return null;

  const classes = { ...defaultClasses, ...customClasses };

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const renderSafeContent = (
    element: Element,
    parentIndex: number,
  ): ReactNode[] => {
    const childNodes = Array.from(element.childNodes);

    return childNodes
      .map((node, nodeIndex) => {
        const uniqueKey = `${parentIndex}-${nodeIndex}`;
         if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          return text ? (
            <span key={`text-${uniqueKey}`} className="font-text">
              {text}
            </span>
          ) : null;
        }

         if (node.nodeType === Node.ELEMENT_NODE) {
          const childElement = node as Element;
          const childTag = childElement.tagName.toLowerCase();

          switch (childTag) {
            case 'strong':
            case 'b':
              return (
                <strong
                  key={`strong-${uniqueKey}`}
                  className={classes.strongClasses}
                >
                  {childElement.textContent}
                </strong>
              );

            case 'a': {
              const href = childElement.getAttribute('href') || '#';
              const isExternal = isExternalLink(href);
              const linkText = childElement.textContent || '';

              if (isExternal) {
                const target = childElement.getAttribute('target') || '_blank';
                const rel =
                  childElement.getAttribute('rel') ||
                  'noopener noreferrer nofollow';

                return (
                  <a
                    key={`link-${uniqueKey}`}
                    className={classes.aClasses}
                    href={href}
                    target={target}
                    rel={rel}
                    aria-label={linkText || 'External link'}
                  >
                    {linkText}
                  </a>
                );
              }

              return (
                <Link
                  key={`link-${uniqueKey}`}
                  href={href}
                  className={classes.aClasses}
                  aria-label={linkText || 'Internal link'}
                >
                  {linkText}
                </Link>
              );
            }

            case 'em':
            case 'i':
              return (
                <em key={`em-${uniqueKey}`} className="italic">
                  {childElement.textContent}
                </em>
              );

            case 'img': {
              const src = childElement.getAttribute('src') || '';
              const alt = childElement.getAttribute('alt') || 'Image';
              const width = childElement.getAttribute('width');
              const height = childElement.getAttribute('height');

              if (!src) return null;

              return (
                <Image
                  key={`img-${uniqueKey}`}
                  className={classes.imgClasses}
                  src={src}
                  alt={alt}
                  width={width ? parseInt(width, 10) : 800}
                  height={height ? parseInt(height, 10) : 400}
                />
              );
            }

            default:
               return (
                <span key={`span-${uniqueKey}`}>
                  {childElement.textContent}
                </span>
              );
          }
        }

        return null;
      })
      .filter(Boolean);
  };

  const renderElement = (
    element: globalThis.Element,
    index: number,
  ): ReactNode => {
    const tagName = element.tagName.toLowerCase();
    const { textContent } = element;
    const content = textContent || '';

    switch (tagName) {
      case 'h1': {
        const safeContent = renderSafeContent(element, index);
        return (
          <h1 key={index} className={classes.h1Classes}>
            {safeContent.length > 0 ? safeContent : content}
          </h1>
        );
      }

      case 'h2': {
        const safeContent = renderSafeContent(element, index);
        return (
          <h2 key={index} className={classes.h2Classes}>
            {safeContent.length > 0 ? safeContent : content}
          </h2>
        );
      }

      case 'h3': {
        const safeContent = renderSafeContent(element, index);
        return (
          <h3 key={index} className={classes.h3Classes}>
            {safeContent.length > 0 ? safeContent : content}
          </h3>
        );
      }

      case 'h4': {
        const safeContent = renderSafeContent(element, index);
        return (
          <h4 key={index} className={classes.h4Classes}>
            {safeContent.length > 0 ? safeContent : content}
          </h4>
        );
      }

      case 'h5': {
        const safeContent = renderSafeContent(element, index);
        return (
          <h5 key={index} className={classes.h5Classes}>
            {safeContent.length > 0 ? safeContent : content}
          </h5>
        );
      }

      case 'h6': {
        const safeContent = renderSafeContent(element, index);
        return (
          <h6 key={index} className={classes.h6Classes}>
            {safeContent.length > 0 ? safeContent : content}
          </h6>
        );
      }

      case 'p': {
        if (!content.trim()) return null;
        const safeContent = renderSafeContent(element, index);
        return (
          <p key={index} className={classes.pClasses}>
            {safeContent.length > 0 ? safeContent : content}
          </p>
        );
      }

      case 'ul':
        return (
          <ul key={index} className={classes.ulClasses}>
            {Array.from(element.children).map((child, childIndex) => {
              return renderElement(child, childIndex);
            })}
          </ul>
        );

      case 'li': {
        const safeContent = renderSafeContent(element, index);
        return (
          <li key={index} className={classes.liClasses}>
            {safeContent.length > 0 ? safeContent : content}
          </li>
        );
      }

      case 'strong': {
        return (
          <strong key={index} className={classes.strongClasses}>
            {content}
          </strong>
        );
      }

      case 'a': {
        const href = element.getAttribute('href') || '#';
        const isExternal = isExternalLink(href);

        if (isExternal) {
          const target = element.getAttribute('target') || '_blank';
          const rel =
            element.getAttribute('rel') || 'noopener noreferrer nofollow';

          return (
            <a
              key={index}
              className={classes.aClasses}
              href={href}
              target={target}
              rel={rel}
              aria-label={content || 'External link'}
            >
              {content}
            </a>
          );
        }

        return (
          <Link
            key={index}
            href={href}
            className={classes.aClasses}
            aria-label={content || 'Internal link'}
          >
            {content}
          </Link>
        );
      }

      case 'img': {
        const src = element.getAttribute('src') || '';
        const alt = element.getAttribute('alt') || 'Image';
        const width = element.getAttribute('width');
        const height = element.getAttribute('height');

        if (!src) return null;

        return (
          <Image
            key={index}
            className={classes.imgClasses}
            src={src}
            alt={alt}
            width={width ? parseInt(width, 10) : 350}
            height={height ? parseInt(height, 10) : 314}
          />
        );
      }

      default:
         return (
          <div key={index} className="mb-2">
            {content}
          </div>
        );
    }
  };

  const bodyElements = Array.from(doc.body.children);

  return (
    <>
      {bodyElements.map((element, index) => {
        return renderElement(element, index);
      })}
    </>
  );
}

HtmlRenderer.displayName = 'HtmlRenderer';

export { HtmlRenderer };

 export function parseHtmlToJSX(htmlString: string): ReactNode[] {
  if (!htmlString) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

   const bodyElements = Array.from(doc.body.children);
  return bodyElements.map((element) => {
     return (
      <div key={`${element.tagName}${element.id ?? ''}`}>
        {element.textContent}
      </div>
    );
  });
}